import {config} from '@config'
import {IComponentGenerator} from 'lowkey-component/dist/component/types'

import {Time} from '@helpers/Time'
import {getPreloadUrls} from '@helpers/dom'
import {sleep} from '@lib/utils'
import {createApplicationState} from '@services/ApplicationState'
import {Preloader} from '@services/loader/Preloader'
import {Pjax, PjaxSignalType} from '@services/navigation/Pjax'
import {IApplicationState} from '@services/types'



type Props = {
  preloader: Preloader;
  generator: IComponentGenerator;
};

export class Navigation {
  static singleton: Navigation
  pjax: Pjax
  generator: IComponentGenerator
  preloader: Preloader
  appState: IApplicationState
  private containerSelector = ''

  static create(props: Props) {
    if (Navigation.singleton) {
      return Navigation.singleton
    }
    return (Navigation.singleton = new Navigation(props))
  }

  constructor({preloader, generator}: Props) {
    this.preloader = preloader
    this.generator = generator
    this.appState = createApplicationState()
    this.pjax = Pjax.create()
    this.pjax.onChange.connect(this.onChange.bind(this))
  }

  private onChange({event, type}: {event: Event; type: PjaxSignalType}) {
    if (type === 'fetch') {
      this._fetch()
    } else if (type === 'ready') {
      this._loaded(event)
    }
  }

  private _fetch() {
    Time.create().start('pageIndicator')
    this.generator.willUnmount()
    this.appState.updatePageIndicatorStatus('show')
  }

  async _loaded(e: Event) {
    // BrowserSyncのrewriteRulesを使った場合は、pjaxで取得したDOMは置換されないので、置換処理が必要。
    // ローカル環境の場合は、localhost:3011をlocalhost:3010に置換する
    // pjaxで取得するhtmlには、localhost:3011が含まれているため、置換してpjaxのイベントをrefreshする必要がある。
    // さらに、fetchで取得する画像はCORSを無効にする必要がある。chromeのエクステンションおすすめ。
    if (process.env.APP_ENV !== 'production' && location.port && location.port.toString() === process.env.BROWSER_SYNC_PORT?.toString()) {
      const list = Array.from(document.querySelectorAll('a[href*=":3011"]')) as HTMLAnchorElement[]
      list.map((element: HTMLAnchorElement) => {
        const exp = new RegExp(`(http:)?//localhost:${process.env.HOST_PORT}`, 'g')
        element.href = element.href.replace(exp, `http://localhost:${process.env.BROWSER_SYNC_PORT}`)
      })
      const container = document.querySelector(this.containerSelector)
      container && this.pjax.refresh(container)
    }

    this.generator.unmount()

    this.generator.refresh()

    const urls = getPreloadUrls()
    await this.preloader.load(urls)

    this.generator.mount()

    const sleepTime = Math.max(
      0,
      config.pageIndicatorHideAnimationDelay -
      Time.create().getDuration('pageIndicator'),
    )

    await sleep(sleepTime)
    this.appState.updatePageIndicatorStatus('hide')
  }

  getContainer() {
    return this.pjax.getContainer()
  }

  start(containerSelector: string) {
    this.containerSelector = containerSelector
    this.pjax.start({
      fetchDelay: config.navigateFetchDelay, // indicatorが表示されてからdomの入れ替え
      selector: containerSelector,
    })
  }

  navigate(url: string) {
    this.pjax.loadUrl(url)
  }
}
